import React, { useState, useEffect } from 'react';
import {
    Card, CardContent, Typography, TextField, Button, Box,
    MenuItem, IconButton, Alert, Divider, FormControlLabel, Checkbox
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { customerAuth } from './customerAuth';

const emptyDetail = { productId: '', quantity: '', deliveryLocationId: '', maxLoad: false };

const CustomerOrderForm = ({ onOrderCreated }) => {
    const [products, setProducts] = useState([]);
    const [locations, setLocations] = useState([]);
    const [orderDetails, setOrderDetails] = useState([{ ...emptyDetail }]);
    const [deliveryDate, setDeliveryDate] = useState('');
    const [poNum, setPoNum] = useState('');
    const [specialInstructions, setSpecialInstructions] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);

    const user = customerAuth.getUser();

    useEffect(() => {
        const load = async () => {
            try {
                const [prodRes, locRes] = await Promise.all([
                    customerAuth.fetchWithAuth('/portal/products'),
                    customerAuth.fetchWithAuth('/portal/deliverylocations')
                ]);
                setProducts(await prodRes.json());
                setLocations(await locRes.json());
            } catch (e) {
                setError('Failed to load form data.');
            }
        };
        load();
    }, []);

    const addDetail = () => setOrderDetails([...orderDetails, { ...emptyDetail }]);

    const removeDetail = (index) => {
        if (orderDetails.length === 1) return;
        setOrderDetails(orderDetails.filter((_, i) => i !== index));
    };

    const updateDetail = (index, field, value) => {
        const updated = [...orderDetails];
        updated[index][field] = value;
        if (field === 'maxLoad' && value) {
            updated[index].quantity = '';
        }
        setOrderDetails(updated);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        var incomplete = orderDetails.some(d => !d.productId || !d.deliveryLocationId || (!d.maxLoad && !d.quantity));
        if (incomplete) {
            setError('Please fill in all fields for each line item (or check Max Load).');
            return;
        }

        setLoading(true);
        try {
            const response = await customerAuth.fetchWithAuth('/portal/orders', {
                method: 'POST',
                body: JSON.stringify({
                    orderDetails: orderDetails.map(d => ({
                        productId: parseInt(d.productId),
                        quantity: d.maxLoad ? 0 : parseInt(d.quantity),
                        deliveryLocationId: parseInt(d.deliveryLocationId),
                        maxLoad: d.maxLoad
                    })),
                    deliveryDate,
                    poNum,
                    specialInstructions
                })
            });
            const data = await response.json();
            if (response.ok) {
                setSuccess(`Order ${data.orderNum} submitted successfully!`);
                setOrderDetails([{ ...emptyDetail }]);
                setDeliveryDate('');
                setPoNum('');
                setSpecialInstructions('');
                if (onOrderCreated) onOrderCreated();
            } else {
                setError(data.message || 'Failed to submit order.');
            }
        } catch (err) {
            setError(err.message);
        }
        setLoading(false);
    };

    return (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom>New Order</Typography>
                {user && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Ordering as: {user.customerName}
                    </Typography>
                )}

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                <form onSubmit={handleSubmit}>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                        <TextField label="PO Number" value={poNum} onChange={e => setPoNum(e.target.value)} sx={{ minWidth: 150 }} />
                        <TextField label="Requested Delivery Date" type="date" value={deliveryDate} onChange={e => setDeliveryDate(e.target.value)}
                            InputLabelProps={{ shrink: true }} required sx={{ minWidth: 200 }} />
                    </Box>

                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>Order Items</Typography>

                    {orderDetails.map((detail, index) => (
                        <Box key={index} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                            <TextField select label="Product" value={detail.productId} onChange={e => updateDetail(index, 'productId', e.target.value)}
                                required sx={{ minWidth: 250, flex: 1 }}>
                                {products.map(p => <MenuItem key={p.id} value={p.id}>{p.Product}</MenuItem>)}
                            </TextField>
                            <TextField label="Quantity" type="number" value={detail.quantity}
                                onChange={e => updateDetail(index, 'quantity', e.target.value)}
                                required={!detail.maxLoad}
                                disabled={false}
                                placeholder={detail.maxLoad ? 'Max' : ''}
                                sx={{ width: 100 }}
                                inputProps={{ min: detail.maxLoad ? 0 : 1 }} />
                            <FormControlLabel
                                control={
                                    <Checkbox checked={detail.maxLoad} onChange={e => updateDetail(index, 'maxLoad', e.target.checked)} size="small" />
                                }
                                label="Max Load"
                                sx={{ mr: 0 }}
                            />
                            <TextField select label="Delivery Location" value={detail.deliveryLocationId} onChange={e => updateDetail(index, 'deliveryLocationId', e.target.value)}
                                required sx={{ minWidth: 250, flex: 1 }}>
                                {locations.map(l => <MenuItem key={l.id} value={l.id}>{l.Delivery_Location} ({l.City})</MenuItem>)}
                            </TextField>
                            <IconButton onClick={() => removeDetail(index)} disabled={orderDetails.length === 1} size="small" color="error">
                                <DeleteIcon />
                            </IconButton>
                        </Box>
                    ))}

                    <Button startIcon={<AddIcon />} onClick={addDetail} size="small" sx={{ mb: 2 }}>
                        Add Item
                    </Button>

                    <Divider sx={{ my: 2 }} />

                    <TextField fullWidth multiline rows={2} label="Special Instructions" value={specialInstructions}
                        onChange={e => setSpecialInstructions(e.target.value)} sx={{ mb: 2 }} />

                    <Button variant="contained" type="submit" disabled={loading} sx={{ backgroundColor: '#0b355d' }}>
                        {loading ? 'Submitting...' : 'Submit Order'}
                    </Button>
                </form>
            </CardContent>
        </Card>
    );
};

export default CustomerOrderForm;
